import Drawer from '@/components/Drawer';
import { FormatDate } from '@/components/PrettyDate';
import { FileModel } from '@/typings/api/files';
import { F } from '@/utils/intl';
import { Button, Descriptions, Form, Input, Space, Typography } from 'antd';
import React, { useEffect } from 'react';
import { formatFileSize, isPreviewable } from './fileDisplay';

import styles from './FileDetailsDrawer.less';

interface FileDetailsDrawerProps {
  file: FileModel | null;
  onClose: () => void;
  onSave: (name: string, description: string | null) => void | Promise<void>;
}

/**
 * What a file is, and the two things about it that can be changed.
 *
 * The bytes are not among them: a file is addressed by a URL that may already
 * be sitting in a sent email, so replacing its content behind that URL would
 * change mail nobody can recall. Replacing a file means uploading a new one.
 */
const FileDetailsDrawer: React.FC<FileDetailsDrawerProps> = ({
  file,
  onClose,
  onSave,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (file) {
      form.setFieldsValue({
        name: file.name,
        description: file.description ?? '',
      });
    }
  }, [file, form]);

  return (
    <Drawer
      open={!!file}
      onOpenChange={(open) => !open && onClose()}
      width={480}
      title={<F id="pages.files.details.title" defaultMessage="File details" />}
    >
      {file && (
        <div className={styles.details}>
          {isPreviewable(file) && (
            <div className={styles.detailsPreview}>
              <img src={file.url} alt={file.name} />
            </div>
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={(values) =>
              onSave(values.name, values.description || null)
            }
          >
            <Form.Item
              name="name"
              label={<F id="pages.files.details.name" defaultMessage="Name" />}
              rules={[{ required: true, max: 250 }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="description"
              label={
                <F
                  id="common.attribute.description"
                  defaultMessage="Description"
                />
              }
              rules={[{ max: 150 }]}
            >
              <Input.TextArea rows={2} />
            </Form.Item>

            <Space>
              <Button type="primary" htmlType="submit">
                <F id="common.actions.save" defaultMessage="Save" />
              </Button>
              <Button onClick={onClose}>
                <F id="common.actions.cancel" defaultMessage="Cancel" />
              </Button>
            </Space>
          </Form>

          <Descriptions
            className={styles.detailsMeta}
            column={1}
            size="small"
            items={[
              {
                key: 'url',
                label: <F id="common.url" defaultMessage="Link" />,
                children: (
                  <Typography.Text copyable className={styles.detailsUrl}>
                    {file.url}
                  </Typography.Text>
                ),
              },
              {
                key: 'type',
                label: (
                  <F id="pages.files.details.type" defaultMessage="Type" />
                ),
                children: file.mime_type ?? '—',
              },
              {
                key: 'size',
                label: (
                  <F id="pages.files.details.size" defaultMessage="Size" />
                ),
                children: formatFileSize(file.size) || '—',
              },
              {
                key: 'created',
                label: (
                  <F
                    id="common.attribute.uploaded_at"
                    defaultMessage="Uploaded"
                  />
                ),
                children: file.created ? (
                  <FormatDate date={file.created} />
                ) : (
                  '—'
                ),
              },
              ...(file.created_by
                ? [
                    {
                      key: 'created_by',
                      label: (
                        <F
                          id="pages.files.details.createdBy"
                          defaultMessage="Uploaded by"
                        />
                      ),
                      children: file.created_by,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      )}
    </Drawer>
  );
};

export default FileDetailsDrawer;
